import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useExpertQuery } from "../../services/hooks/experts/queries/useExpertQuery";
import { FormGroup } from "../FormElements/FormGroup";
import { Button } from "../Button";
import {
  internationalBankDataSchema,
  validateBankDataOrAmazonGiftEmailIsFilled,
} from "../../validators/internationalBankDataSchema";
import { type InternationalBankDataPersistence } from "../../types/persistence/international-person-persistence";
import { PaymentTypeEnum } from "../../enumns/paymentType";

type BankDataProps = {
  isSubmitting: boolean;
  defaultValues?: InternationalBankDataPersistence;
  onSubmit: (data: InternationalBankDataPersistence) => void;
  onCancel?: () => void;
  onGoPreviousStep?: () => void;
  userId?: number;
};

export const InternationalBankDataForm = ({
  isSubmitting,
  defaultValues,
  onSubmit,
  onGoPreviousStep,
  onCancel,
  userId,
}: BankDataProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const { data } = useExpertQuery(userId!);

  const internationalBankDataYupSchema =
    data?.paymentType !== PaymentTypeEnum.NO_PAYMENT
      ? internationalBankDataSchema.test(
          "at-least-one",
          "INVALID_INTERNATIONAL_BANK_DATA",
          validateBankDataOrAmazonGiftEmailIsFilled
        )
      : internationalBankDataSchema;

  const { control, formState, handleSubmit } =
    useForm<InternationalBankDataPersistence>({
      resolver: yupResolver(internationalBankDataYupSchema),
      mode: "onChange",
      defaultValues,
      resetOptions: {
        keepDirtyValues: true,
      },
    });

  return (
    <div className="flex flex-col w-full h-full">
      <form onSubmit={handleSubmit(onSubmit)} className="max-w-[640px] flex flex-col gap-2">
        <div className="mb-4">
          <Controller
            name="swiftCode"
            control={control}
            render={({
              field: { value = "", onChange },
              fieldState: { error },
            }) => (
              <FormGroup
                label={formatMessage({ id: "SWIFT_CODE" })}
                error={error?.message && formatMessage({ id: error.message })}
              >
                <input
                  type="text"
                  className="w-full p-3 text-sm rounded-lg lg:text-base border-1 border-grey-500"
                  onChange={onChange}
                  value={value}
                />
              </FormGroup>
            )}
          />
        </div>

        <div className="mb-4">
          <Controller
            name="bankAccountNumber"
            control={control}
            render={({
              field: { value = "", onChange },
              fieldState: { error },
            }) => (
              <FormGroup
                label={formatMessage({ id: "ACCOUNT_NUMBER_OR_IBAN" })}
                error={error?.message && formatMessage({ id: error.message })}
              >
                <input
                  type="text"
                  className="w-full p-3 text-sm rounded-lg lg:text-base border-1 border-grey-500"
                  onChange={onChange}
                  value={value}
                />
              </FormGroup>
            )}
          />
        </div>

        <div className="mb-4">
          <Controller
            name="internationalBankBranch"
            control={control}
            render={({
              field: { value = "", onChange },
              fieldState: { error },
            }) => (
              <FormGroup
                label={formatMessage({ id: "INTERNATIONAL_BANK_BRANCH" })}
                error={error?.message && formatMessage({ id: error.message })}
              >
                <input
                  type="text"
                  className="w-full p-3 text-sm rounded-lg lg:text-base border-1 border-grey-500"
                  onChange={onChange}
                  value={value}
                />
              </FormGroup>
            )}
          />
        </div>

        <div className="mb-4">
          <Controller
            name="amazonGiftCardEmail"
            control={control}
            render={({
              field: { value = "", onChange },
              fieldState: { error },
            }) => (
              <FormGroup
                label={formatMessage({ id: "AMAZON_GIFT_CARD_EMAIL" })}
                error={error?.message && formatMessage({ id: error.message })}
              >
                <input
                  type="text"
                  className="w-full p-3 text-sm rounded-lg lg:text-base border-1 border-grey-500"
                  onChange={onChange}
                  placeholder="example@email.com.br"
                  value={value}
                />
              </FormGroup>
            )}
          />
        </div>

        <div className="flex flex-row mt-14 gap-4 justify-center">
          <Button
            type="button"
            onClick={onGoPreviousStep || (() => navigate("/expert"))}
            variant="outline"
            borderColor="border-primary-light"
            size="large"
            className="ps-12 pe-12"
          >
            {formatMessage({ id: "BACK" })}
          </Button>

          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={
              !formState.isValid &&
              data?.paymentType !== PaymentTypeEnum.NO_PAYMENT
            }
            size="large"
            className="ps-12 pe-12"
          >
            {formatMessage({ id: "SAVE" })}
          </Button>
        </div>
      </form>
    </div>
  );
};
